import React from 'react';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle } from '../theme';
import OCRScanner from '../components/OCRScanner';

/**
 * Patient registration tab — 3-step form (Basic / History / Contact).
 */
const RegisterPatientPage = ({ app }) => {
  const [showOCR, setShowOCR] = React.useState(false);
  const {
    regStep, setRegStep,
    patient, setPatient,
    capturedPhoto, showWebcam,
    videoRef, startWebcam, capturePhoto, stopWebcam, retakePhoto,
    handleSavePatient,
    medicalList, dentalList, medicinesList, allergiesList,
    ratePlans, groupsList, tagsList,
    regFeeConfig,
  } = app;

  // Resolved fee amount — override if user typed a custom value, else fall
  // back to the clinic default. Empty string in the input means "use default".
  const feeAmount = patient?.regFee?.amount !== '' && patient?.regFee?.amount != null
    ? Number(patient.regFee.amount)
    : Number(regFeeConfig?.amount || 0);
  const feeValidityDays = Number(regFeeConfig?.validityDays || 15);
  const feeValidTill = new Date(Date.now() + feeValidityDays * 86400000)
    .toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });

  return (
    <div style={cardStyle}>
      {/* Step Indicator */}
      <div style={{display:'flex', alignItems:'center', marginBottom:'30px'}}>
        {[{n:1,label:'Basic Info'},{n:2,label:'Medical History'},{n:3,label:'Contact'}].map(({n,label},i) => (
          <React.Fragment key={n}>
            <div onClick={()=>setRegStep(n)} style={{display:'flex', flexDirection:'column', alignItems:'center', cursor:'pointer'}}>
              <div style={{width:'36px', height:'36px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'bold', fontSize:'0.95rem',
                backgroundColor: regStep === n ? theme.primary : regStep > n ? theme.success : '#e9ecef',
                color: regStep >= n ? 'white' : '#999'}}>
                {regStep > n ? '✓' : n}
              </div>
              <span style={{fontSize:'0.75rem', marginTop:'4px', color: regStep === n ? theme.primary : '#999', fontWeight: regStep === n ? 'bold' : 'normal'}}>{label}</span>
            </div>
            {i < 2 && <div style={{flex:1, height:'2px', backgroundColor: regStep > n ? theme.success : '#e9ecef', margin:'0 8px', marginBottom:'18px'}}/>}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1: Basic Info */}
      {regStep === 1 && (
        <div>
          <div style={{display:'flex', alignItems:'center', gap:'25px', marginBottom:'25px'}}>
            <div style={{width:'110px', height:'110px', borderRadius:'50%', border:'2px dashed #ccc', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', backgroundColor:'#f9f9f9', flexShrink:0}}>
              {capturedPhoto ? <img src={capturedPhoto} alt="patient" style={{width:'100%', height:'100%', objectFit:'cover'}}/> : <span style={{color:'#aaa', fontSize:'0.75rem', textAlign:'center'}}>No Photo</span>}
            </div>
            {showWebcam ? (
              <div>
                <video ref={videoRef} autoPlay style={{width:'220px', borderRadius:'8px', border:'2px solid #00a6fb'}}/>
                <div style={{display:'flex', gap:'8px', marginTop:'8px'}}>
                  <button onClick={capturePhoto} style={{...btnStyle, marginTop:0, backgroundColor:theme.success, padding:'7px 14px'}}>Capture</button>
                  <button onClick={stopWebcam} style={{...btnStyle, marginTop:0, backgroundColor:theme.danger, padding:'7px 14px'}}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{display:'flex', gap:'8px'}}>
                <button onClick={startWebcam} style={{...btnStyle, marginTop:0, padding:'7px 14px'}}>Open Camera</button>
                {capturedPhoto && <button onClick={retakePhoto} style={{...btnStyle, marginTop:0, backgroundColor:'#6c757d', padding:'7px 14px'}}>Retake</button>}
              </div>
            )}
          </div>

          {/* Scan ID Button */}
          <div style={{marginBottom:'16px'}}>
            <button onClick={()=>setShowOCR(true)}
              style={{padding:'10px 22px', background:'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'700', fontSize:'0.9rem', boxShadow:'0 2px 8px rgba(245,158,11,0.3)'}}>
              📷 Scan ID Card (Aadhaar / PAN / DL)
            </button>
            <span style={{marginLeft:'10px', fontSize:'0.78rem', color:theme.textMuted}}>Auto-fill name, DOB, gender, address from ID card photo</span>
          </div>

          {/* OCR Modal */}
          {showOCR && (
            <OCRScanner
              mode="registration"
              onDataExtracted={(parsed) => {
                setPatient(p => ({
                  ...p,
                  ...(parsed.name ? { name: parsed.name } : {}),
                  ...(parsed.dob ? { dob: parsed.dob, age: parsed.age || '', approxAge: false } : {}),
                  ...(parsed.gender ? { gender: parsed.gender } : {}),
                  ...(parsed.address ? { address: parsed.address } : {}),
                  ...(parsed.aadhaar ? { aadhaar: parsed.aadhaar } : {}),
                }));
                setShowOCR(false);
              }}
              onClose={()=>setShowOCR(false)}
            />
          )}

          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'15px'}}>
            <div>
              <label style={labelStyle}>Name *</label>
              <div style={{display:'flex', gap:'6px'}}>
                <select value={patient.title}
                  onChange={e=>{
                    const t = e.target.value;
                    const gm = {'Mr.':'Male','Mrs.':'Female','Ms.':'Female','Master':'Male','Baby':''};
                    setPatient({...patient, title:t, gender:gm[t]??patient.gender});
                  }}
                  style={{...inputStyle, width:'90px'}}>
                  <option value="">Title</option>
                  <option>Mr.</option><option>Mrs.</option><option>Ms.</option><option>Master</option><option>Baby</option>
                </select>
                <input placeholder="Patient Name" value={patient.name} onChange={e=>setPatient({...patient,name:e.target.value})} style={inputStyle}/>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Phone *</label>
              <input placeholder="Mobile Number" value={patient.phone} onChange={e=>setPatient({...patient,phone:e.target.value})} style={inputStyle}/>
              <label style={{display:'flex', alignItems:'center', gap:'6px', marginTop:'7px', cursor:'pointer'}}>
                <input type="checkbox" checked={patient.smsDnd} onChange={e=>setPatient({...patient,smsDnd:e.target.checked})} style={{width:'15px',height:'15px'}}/>
                <span style={{fontSize:'0.82rem', color: patient.smsDnd?theme.danger:'#555', fontWeight: patient.smsDnd?'bold':'normal'}}>{patient.smsDnd ? '🚫 SMS DND Active' : 'SMS DND'}</span>
              </label>
            </div>
            <div>
              <label style={labelStyle}>Gender</label>
              <select value={patient.gender} onChange={e=>setPatient({...patient,gender:e.target.value})} style={inputStyle}>
                <option value="">Select Gender</option>
                <option>Male</option><option>Female</option><option>Other</option>
              </select>
            </div>
            <div style={{gridColumn:'span 2'}}>
              <label style={labelStyle}>Date of Birth / Age</label>
              <div style={{display:'flex', gap:'8px', alignItems:'center', marginTop:'4px', flexWrap:'wrap'}}>
                <input type="date" value={patient.dob}
                  onChange={e=>{
                    const dob = e.target.value;
                    const age = dob ? Math.floor((new Date()-new Date(dob))/(365.25*24*60*60*1000)) : '';
                    setPatient({...patient, dob, age, approxAge:false});
                  }}
                  style={{...inputStyle, maxWidth:'175px'}}/>
                <span style={{color:'#bbb'}}>or</span>
                <input type="number" placeholder="Approx Age" min="0" max="120"
                  value={patient.approxAge?patient.age:''}
                  onChange={e=>setPatient({...patient, age:e.target.value, dob:'', approxAge:true})}
                  style={{...inputStyle, maxWidth:'120px'}}/>
                <span style={{fontSize:'0.85rem',color:'#555'}}>yrs</span>
                {patient.dob && !patient.approxAge && patient.age!=='' && <span style={{backgroundColor:'#e8f4fd',color:'#0582ca',padding:'5px 12px',borderRadius:'20px',fontSize:'0.85rem',fontWeight:'bold'}}>Age: {patient.age} yrs</span>}
              </div>
            </div>
            <div><label style={labelStyle}>Aadhaar</label><input placeholder="Aadhaar Number" value={patient.aadhaar} onChange={e=>setPatient({...patient,aadhaar:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Father/Husband Name</label><input placeholder="Pita/Pati ka Naam" value={patient.fatherHusbandName||''} onChange={e=>setPatient({...patient,fatherHusbandName:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Occupation</label><input placeholder="Occupation" value={patient.occupation} onChange={e=>setPatient({...patient,occupation:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Referred By</label><input placeholder="Referred By" value={patient.referredBy} onChange={e=>setPatient({...patient,referredBy:e.target.value})} style={inputStyle}/></div>
            <div>
              <label style={labelStyle}>Rate Card Plan</label>
              <select value={patient.ratePlan} onChange={e=>setPatient({...patient,ratePlan:e.target.value})} style={inputStyle}>
                <option value="">-- Select Plan --</option>
                {ratePlans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{display:'flex', justifyContent:'flex-end', marginTop:'25px'}}>
            <button onClick={()=>setRegStep(2)} style={btnStyle}>Next: Medical History →</button>
          </div>
        </div>
      )}

      {/* STEP 2: Medical History */}
      {regStep === 2 && (
        <div>
          {[
            {label:'Medical History',   field:'medicalHistory',   list:medicalList,    color:theme.danger,  placeholder:'Add another condition...'},
            {label:'Dental History',    field:'dentalHistory',    list:dentalList,     color:theme.warning, placeholder:'Add another dental issue...'},
            {label:'Current Medicines', field:'currentMedicines', list:medicinesList,  color:theme.purple,  placeholder:'Medicine name...'},
            {label:'Allergies',         field:'allergies',        list:allergiesList,  color:theme.pink,    placeholder:'Allergy...'},
          ].map(({label, field, list, color, placeholder}) => (
            <div key={field} style={{marginBottom:'20px'}}>
              <h4 style={{color, marginBottom:'8px'}}>{label}</h4>
              <div style={{padding:'14px', border:`1px solid ${color}33`, borderRadius:'10px', backgroundColor:`${color}06`}}>
                <div style={{display:'flex', flexWrap:'wrap', gap:'8px', marginBottom: list.length?'10px':'0'}}>
                  {list.map(item => {
                    const sel = patient[field].includes(item.label);
                    return (
                      <span key={item._id}
                        onClick={()=>{
                          const u = sel ? patient[field].filter(x=>x!==item.label) : [...patient[field], item.label];
                          setPatient({...patient, [field]:u});
                        }}
                        style={{padding:'5px 14px', borderRadius:'20px', cursor:'pointer', fontSize:'0.85rem', fontWeight:'500',
                          backgroundColor: sel?color:'#fff', color: sel?'white':'#555', border:`2px solid ${sel?color:'#dee2e6'}`}}>
                        {item.label}
                      </span>
                    );
                  })}
                  {list.length === 0 && <span style={{color:'#aaa',fontSize:'0.82rem'}}>Add options in Settings</span>}
                </div>
                <input placeholder={placeholder}
                  style={{...inputStyle, marginTop: list.length?'8px':'0'}}
                  onKeyDown={e=>{
                    if (e.key==='Enter' && e.target.value.trim()) {
                      if (!patient[field].includes(e.target.value.trim())) {
                        setPatient({...patient, [field]:[...patient[field], e.target.value.trim()]});
                      }
                      e.target.value = '';
                    }
                  }}/>
                <div style={{display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'8px'}}>
                  {patient[field].filter(x => !list.map(l=>l.label).includes(x)).map((item, i) => (
                    <span key={i} style={{display:'inline-flex', alignItems:'center', gap:'5px', padding:'4px 10px', backgroundColor:color, color:'white', borderRadius:'20px', fontSize:'0.82rem'}}>
                      {item}
                      <span onClick={()=>setPatient({...patient, [field]:patient[field].filter(x=>x!==item)})} style={{cursor:'pointer', fontWeight:'bold'}}>×</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {/* Habits, Pregnancy, Breastfeeding */}
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'15px', marginTop:'10px', padding:'14px', border:'1px solid #e9ecef', borderRadius:'10px', backgroundColor:'#fafafa'}}>
            <div>
              <label style={{...labelStyle, marginBottom:'8px', display:'block'}}>Habits / Aadate</label>
              <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
                {['Smoking', 'Tobacco', 'Alcohol', 'Other'].map(h => {
                  const sel = (patient.habits||[]).includes(h);
                  return <span key={h} onClick={()=>{const u=sel?(patient.habits||[]).filter(x=>x!==h):[...(patient.habits||[]),h]; setPatient({...patient,habits:u});}} style={{padding:'5px 14px',borderRadius:'20px',cursor:'pointer',fontSize:'0.85rem',fontWeight:'500',backgroundColor:sel?theme.danger:'#fff',color:sel?'white':'#555',border:`2px solid ${sel?theme.danger:'#dee2e6'}`}}>{h}</span>;
                })}
              </div>
            </div>
            <div>
              <label style={{...labelStyle, marginBottom:'8px', display:'block'}}>Pregnant?</label>
              <div style={{display:'flex', gap:'8px'}}>
                {['Yes', 'No'].map(v => (
                  <span key={v} onClick={()=>setPatient({...patient,isPregnant:v})} style={{padding:'5px 14px',borderRadius:'20px',cursor:'pointer',fontSize:'0.85rem',fontWeight:'500',backgroundColor:patient.isPregnant===v?theme.purple:'#fff',color:patient.isPregnant===v?'white':'#555',border:`2px solid ${patient.isPregnant===v?theme.purple:'#dee2e6'}`}}>{v}</span>
                ))}
              </div>
            </div>
            <div>
              <label style={{...labelStyle, marginBottom:'8px', display:'block'}}>Breastfeeding?</label>
              <div style={{display:'flex', gap:'8px'}}>
                {['Yes', 'No'].map(v => (
                  <span key={v} onClick={()=>setPatient({...patient,isBreastfeeding:v})} style={{padding:'5px 14px',borderRadius:'20px',cursor:'pointer',fontSize:'0.85rem',fontWeight:'500',backgroundColor:patient.isBreastfeeding===v?theme.pink:'#fff',color:patient.isBreastfeeding===v?'white':'#555',border:`2px solid ${patient.isBreastfeeding===v?theme.pink:'#dee2e6'}`}}>{v}</span>
                ))}
              </div>
            </div>
          </div>

          <div style={{display:'flex', justifyContent:'space-between', marginTop:'10px'}}>
            <button onClick={()=>setRegStep(1)} style={{...btnStyle, backgroundColor:'#6c757d'}}>← Back</button>
            <button onClick={()=>setRegStep(3)} style={btnStyle}>Next: Contact →</button>
          </div>
        </div>
      )}

      {/* STEP 3: Contact */}
      {regStep === 3 && (
        <div>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px', marginBottom:'20px'}}>
            <div style={{gridColumn:'span 2'}}>
              <label style={labelStyle}>Address</label>
              <input placeholder="Full Address" value={patient.address} onChange={e=>setPatient({...patient,address:e.target.value})} style={inputStyle}/>
            </div>
            <div>
              <label style={labelStyle}>Insurance Company</label>
              <input placeholder="Company Name" value={patient.insurance.companyName}
                onChange={e=>setPatient({...patient, insurance:{...patient.insurance, companyName:e.target.value}})}
                style={inputStyle}/>
            </div>
            <div>
              <label style={labelStyle}>Insurance Type</label>
              <input placeholder="Sub Type" value={patient.insurance.subType}
                onChange={e=>setPatient({...patient, insurance:{...patient.insurance, subType:e.target.value}})}
                style={inputStyle}/>
            </div>
          </div>

          <div style={{marginBottom:'20px'}}>
            <label style={labelStyle}>Notes</label>
            <textarea placeholder="Any additional notes..." value={patient.notes}
              onChange={e=>setPatient({...patient,notes:e.target.value})}
              style={{...inputStyle, height:'80px', resize:'vertical', marginTop:'4px'}}/>
          </div>

          <div style={{border:`2px solid ${theme.primary}`, borderRadius:'12px', padding:'20px', backgroundColor:theme.primaryLight}}>
            <h4 style={{color:theme.navy, marginTop:0, marginBottom:'18px'}}>Relationship</h4>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px'}}>
              <div>
                <label style={labelStyle}>Patient Since</label>
                <input type="date" value={patient.patientSince} onChange={e=>setPatient({...patient,patientSince:e.target.value})} style={inputStyle}/>
              </div>
              <div>
                <label style={labelStyle}>Groups</label>
                <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
                  {groupsList.map(g => {
                    const sel = patient.groups.includes(g.label);
                    return (
                      <span key={g._id}
                        onClick={()=>setPatient({...patient, groups: sel ? patient.groups.filter(x=>x!==g.label) : [...patient.groups, g.label]})}
                        style={{padding:'5px 12px', borderRadius:'20px', cursor:'pointer', fontSize:'0.85rem', fontWeight:'500',
                          backgroundColor: sel?theme.navy:'#e9ecef', color: sel?'white':'#555'}}>
                        {g.label}
                      </span>
                    );
                  })}
                  {groupsList.length === 0 && <span style={{color:'#aaa', fontSize:'0.85rem'}}>Create groups in Settings</span>}
                </div>
              </div>
              <div style={{gridColumn:'span 2'}}>
                <label style={labelStyle}>Tags</label>
                <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
                  {tagsList.map(t => {
                    const sel = patient.tags.includes(t.label);
                    return (
                      <span key={t._id}
                        onClick={()=>setPatient({...patient, tags: sel ? patient.tags.filter(x=>x!==t.label) : [...patient.tags, t.label]})}
                        style={{padding:'5px 12px', borderRadius:'20px', cursor:'pointer', fontSize:'0.85rem', fontWeight:'500',
                          backgroundColor: sel?'#0582ca':'#e9ecef', color: sel?'white':'#555'}}>
                        #{t.label}
                      </span>
                    );
                  })}
                  {tagsList.length === 0 && <span style={{color:'#aaa', fontSize:'0.85rem'}}>Create tags in Settings</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Registration / OPD Fee — only shown when clinic has it enabled */}
          {regFeeConfig?.enabled && (
            <div style={{marginTop:'20px', border:`2px solid ${patient?.regFee?.collect ? theme.success : '#e9ecef'}`,
              borderRadius:'12px', padding:'18px',
              backgroundColor: patient?.regFee?.collect ? '#e8f7ef' : '#fafafa'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
                <h4 style={{margin:0, color:theme.navy}}>🧾 {regFeeConfig.label || 'Registration / OPD Fee'}</h4>
                <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer', fontSize:'0.85rem'}}>
                  <input type="checkbox"
                    checked={!!patient?.regFee?.collect}
                    onChange={e => setPatient({...patient, regFee: {...(patient.regFee||{}), collect: e.target.checked}})}
                    style={{width:'16px', height:'16px'}}/>
                  <span style={{fontWeight: 600, color: patient?.regFee?.collect ? theme.success : '#777'}}>
                    {patient?.regFee?.collect ? 'Collect fee now' : 'Waive (no charge)'}
                  </span>
                </label>
              </div>

              {patient?.regFee?.collect ? (
                <>
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px'}}>
                    <div>
                      <label style={labelStyle}>Amount (₹)</label>
                      <input type="number" min="0" placeholder={`Default ${regFeeConfig.amount}`}
                        value={patient?.regFee?.amount ?? ''}
                        onChange={e => setPatient({...patient, regFee: {...(patient.regFee||{}), amount: e.target.value}})}
                        style={inputStyle}/>
                    </div>
                    <div>
                      <label style={labelStyle}>Payment Mode</label>
                      <select value={patient?.regFee?.mode || 'cash'}
                        onChange={e => setPatient({...patient, regFee: {...(patient.regFee||{}), mode: e.target.value}})}
                        style={inputStyle}>
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="upi">UPI</option>
                        <option value="cheque">Cheque</option>
                        <option value="bank-transfer">Bank Transfer</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Reference (optional)</label>
                      <input placeholder="UPI ref / cheque no"
                        value={patient?.regFee?.referenceNumber || ''}
                        onChange={e => setPatient({...patient, regFee: {...(patient.regFee||{}), referenceNumber: e.target.value}})}
                        style={inputStyle}/>
                    </div>
                  </div>
                  <div style={{marginTop:'12px', padding:'10px 14px', backgroundColor:'#fff',
                    borderRadius:'8px', border:'1px solid #d4edda', fontSize:'0.85rem', color:'#155724'}}>
                    ✓ <strong>₹{feeAmount}</strong> auto-charge hogi. Valid till <strong>{feeValidTill}</strong>
                    {' '}({feeValidityDays} days). Expiry ke baad jab patient wapas aayega to next bill me dobara add hogi.
                  </div>
                </>
              ) : (
                <div style={{padding:'10px 14px', backgroundColor:'#fff3cd', borderRadius:'8px',
                  border:'1px solid #ffc107', fontSize:'0.85rem', color:'#856404'}}>
                  ⚠️ Fee waive ki gayi. Patient ki OPD validity NOT set hogi — next bill par auto-recharge trigger hoga.
                </div>
              )}
            </div>
          )}

          <div style={{display:'flex', justifyContent:'space-between', marginTop:'25px'}}>
            <button onClick={()=>setRegStep(2)} style={{...btnStyle, backgroundColor:'#6c757d'}}>← Back</button>
            <button onClick={handleSavePatient} style={{...btnStyle, backgroundColor:theme.success}}>Save Patient ✓</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPatientPage;
